import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { exposeStrapiError } from 'utils'
import { ReadTrainingApprovals } from 'services/training-approval'

const PAGE_SIZE = 10

function getHomeRoute(pathname) {
  if (pathname?.startsWith('/mvu-lite') || pathname?.startsWith('/mvu-essentials')) return '/mvu-lite'
  if (pathname?.startsWith('/mvu-accelerator')) return '/mvu-accelerator'
  return '/dashboard'
}

export default function useController() {
  const history = useHistory()
  const location = useLocation()
  const requestIdRef = useRef(0)
  const [summary, setSummary] = useState(null)
  const [authorized, setAuthorized] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState('pending')

  const loadSummary = useCallback(async ({ silent = false } = {}) => {
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId
    if (!silent) setLoading(true)

    const result = await ReadTrainingApprovals({
      page,
      pageSize: PAGE_SIZE,
      search: debouncedSearch,
      status,
    })
    if (requestId !== requestIdRef.current) return

    if (!result.ok) {
      setSummary(null)
      if (result.status === 401 || result.status === 403) {
        setAuthorized(false)
        history.replace(getHomeRoute(location.pathname))
      } else {
        setAuthorized(true)
        setError('Training approvals could not be loaded. Please try again.')
        exposeStrapiError(result.data)
      }
    } else {
      setAuthorized(true)
      setSummary(result.data || null)
      setError('')
      const responsePage = Number(result?.data?.pagination?.page)
      if (Number.isInteger(responsePage) && responsePage !== page) setPage(responsePage)
    }
    setLoading(false)
    setLoaded(true)
  }, [debouncedSearch, history, location.pathname, page, status])

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setPage(1)
      setDebouncedSearch(search.trim())
    }, 300)
    return () => window.clearTimeout(timeout)
  }, [search])

  useEffect(() => {
    loadSummary()
  }, [loadSummary])

  const handleStatusChange = useCallback(nextStatus => {
    setPage(1)
    setStatus(nextStatus)
  }, [])

  const handleReview = useCallback(documentId => {
    const base = location.pathname.replace(/\/training-approvals\/?$/, '')
    history.push(`${base}/training-approvals/${documentId}`)
  }, [history, location.pathname])

  return {
    summary,
    authorized,
    loading,
    loaded,
    error,
    search,
    status,
    retry: loadSummary,
    onSearchChange: event => setSearch(event.target.value),
    onPageChange: (event, nextPage) => setPage(nextPage + 1),
    onStatusChange: handleStatusChange,
    onReview: handleReview,
  }
}
